/** Default online max players when customer type maximum_booking_size is empty. */
export const DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE = 4;

/**
 * Effective online max players for the authenticated booker.
 * Empty/null type config defaults to the tee time size (fallback 4),
 * then capped by facility max_booking_size.
 */
export function getEffectiveMaximumBookingSize(
  customerMaximumBookingSize?: number | null,
  facilityMaxBookingSize?: number | null,
  teeTimeSize?: number | null,
): number {
  const typeMax =
    customerMaximumBookingSize == null || Number(customerMaximumBookingSize) < 1
      ? Number(teeTimeSize) >= 1
        ? Number(teeTimeSize)
        : DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE
      : Number(customerMaximumBookingSize);

  const facilityMax =
    facilityMaxBookingSize == null || Number(facilityMaxBookingSize) < 1
      ? 16
      : Number(facilityMaxBookingSize);

  return Math.min(typeMax, facilityMax);
}

/**
 * Max players for tee-time list filters: largest quantity remaining on any single tee time,
 * capped by facility max_booking_size (legacy display floor of 4, ceiling of 6).
 */
export function getSingleTeeFilterMaxPlayers(
  quantityRemainings: number[],
  facilityMaxBookingSize?: number | null,
): number {
  const settingsMax =
    facilityMaxBookingSize == null || Number(facilityMaxBookingSize) < 1
      ? DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE
      : Number(facilityMaxBookingSize);
  const dataMax =
    quantityRemainings.length > 0 ? Math.max(...quantityRemainings.map(q => Number(q) || 0)) : 0;
  return Math.min(settingsMax, Math.max(dataMax, DEFAULT_CUSTOMER_MAXIMUM_BOOKING_SIZE), 6);
}

export type TConsecutiveCapacityTeeTime = {
  id: number;
  division_id: number;
  start_time: string;
  quantity_remaining: number;
  blocked_type?: string | null;
};

/**
 * Sum capacity of consecutive same-division tee times starting at the selected tee time.
 * Stops at the first gap (empty, blocked, or different division already excluded).
 */
export function getConsecutiveSameDivisionCapacity(
  teeTimes: TConsecutiveCapacityTeeTime[],
  selectedTeeTimeId: number,
  maxNeeded?: number,
): number {
  const selected = teeTimes.find(teeTime => teeTime.id === selectedTeeTimeId);
  if (!selected) {
    return 0;
  }

  const sameDivision = [...teeTimes]
    .filter(teeTime => teeTime.division_id === selected.division_id)
    .sort((a, b) => String(a.start_time).localeCompare(String(b.start_time)));

  const startIndex = sameDivision.findIndex(teeTime => teeTime.id === selectedTeeTimeId);
  if (startIndex < 0) {
    return 0;
  }

  let total = 0;
  for (let i = startIndex; i < sameDivision.length; i++) {
    const teeTime = sameDivision[i];
    if (["blocked", "crossover"].includes(String(teeTime.blocked_type ?? ""))) {
      break;
    }
    const qty = Number(teeTime.quantity_remaining ?? 0);
    if (!Number.isFinite(qty) || qty <= 0) {
      break;
    }
    total += qty;
    if (maxNeeded != null && total >= maxNeeded) {
      return total;
    }
  }
  return total;
}

/**
 * Sum consecutive open capacity starting at index (stops at first empty tee time).
 * Prefer getConsecutiveSameDivisionCapacity when division grouping matters.
 */
export function getConsecutiveOpenCapacity(
  quantityRemainings: number[],
  startIndex: number,
  maxNeeded?: number,
): number {
  let total = 0;
  for (let i = startIndex; i < quantityRemainings.length; i++) {
    const qty = Number(quantityRemainings[i] ?? 0);
    if (!Number.isFinite(qty) || qty <= 0) {
      break;
    }
    total += qty;
    if (maxNeeded != null && total >= maxNeeded) {
      return total;
    }
  }
  return total;
}
